import React, { useState, useEffect } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode, getAuth } from 'firebase/auth';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const showToast = useToast();
  const auth = getAuth();

  const resetCode = searchParams.get('oobCode');

  // Verify the reset code when component mounts
  useEffect(() => {
    const verifyCode = async () => {
      if (!resetCode) {
        setMessage('Invalid or missing reset link');
        setMessageType('error');
        setVerifying(false);
        return;
      }

      try {
        await verifyPasswordResetCode(auth, resetCode);
        setCodeValid(true);
      } catch (error) {
        console.error('Error verifying code:', error);
        let errorMessage = 'Invalid or expired reset link';
        if (error.code === 'auth/expired-action-code') {
          errorMessage = 'This reset link has expired. Please request a new one.';
        } else if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'This reset link is invalid. Please request a new one.';
        }
        setMessage(errorMessage);
        setMessageType('error');
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [resetCode, auth]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setMessageType('error');
      showToast('Passwords do not match', 'error');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setMessageType('error');
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, resetCode, newPassword);
      setMessage('✓ Password reset successfully! Redirecting to login...');
      setMessageType('success');
      showToast('Password reset successfully', 'success');

      // Redirect to home page after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error resetting password:', error);
      let errorMessage = 'Error resetting password';
      if (error.code === 'auth/expired-action-code') {
        errorMessage = 'This reset link has expired. Please request a new one.';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This reset link is invalid.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Use a stronger password.';
      }
      setMessage(errorMessage);
      setMessageType('error');
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!codeValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">Invalid Reset Link</h2>
            <div className={`p-4 mb-4 rounded-md bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`}>
              {message}
            </div>
            <Link
              to="/forgot-password"
              className="inline-block mt-4 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition duration-200"
            >
              Request New Reset Link
            </Link>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
              <Link to="/" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                Back to Home
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-8">
        <h2 className="text-3xl font-bold mb-2 text-indigo-700 dark:text-indigo-300 text-center">
          Create New Password
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 text-sm">
          Enter a strong password to protect your account
        </p>

        {message && (
          <div className={`p-4 mb-4 rounded-md ${
            messageType === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {message}
          </div>
        )}

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">At least 6 characters</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          <Link to="/" className="text-indigo-600 dark:text-indigo-400 hover:underline">
            Back to Home
          </Link>
        </p>
      </div>
    </div>
  );
}
